<template>
    <div>
        <b-container class="pl-0">
            <b-row v-if="!source">
                <b-col cols="6" md="4" class="d-flex">
                    <label for="_fieldInput">{{
                        $t('styling.patterns.titlePolygonPattern')
                    }}</label>
                </b-col>
                <b-col>
                    <x-select
                        :id="`_fieldInput`"
                        :name="`_fieldInput`"
                        v-model="localSettings.nameSvg"
                        :options="fieldInputOptions"
                        :simpleMode="true"
                        @input="$_setSvgNameAndSource($event)"
                    >
                    </x-select>
                </b-col>
            </b-row>
            <template v-if="selectedSource">
                <b-row class="pb-1">
                    <b-col cols="6" md="4">
                        <label for="size">{{ $t('styling.size') }}</label>
                    </b-col>
                    <b-col class="d-flex">
                        <input
                            id="size"
                            type="range"
                            class="form-range"
                            :min="sizeMin"
                            :max="sizeMax"
                            step="1"
                            v-model="localSettings.sizeSvg"
                        />
                        <span class="pl-1">
                            {{ `${localSettings.sizeSvg}` }} </span
                        >{{ localSettings.sizeUnits }}
                    </b-col>
                </b-row>
                <b-row v-if="hasPointGeometry">
                    <b-col cols="6" md="4" class="d-flex"></b-col>
                    <b-col>
                        <b-form-radio-group
                            v-model="localSettings.sizeUnits"
                            @change="$_updateSizeUnits"
                        >
                            <b-form-radio :value="iconUnits.pixels">{{
                                $t('unitPixel')
                            }}</b-form-radio>
                            <b-form-radio :value="iconUnits.meters">{{
                                $t('unitMeter')
                            }}</b-form-radio>
                        </b-form-radio-group>
                    </b-col>
                </b-row>
                <b-row v-if="hasFill">
                    <b-col cols="6" md="4" class="d-flex"
                        ><label for="fill">{{
                            $t('styling.backgroundColor')
                        }}</label></b-col
                    >
                    <b-col>
                        <color-picker
                            id="fill"
                            name="fill"
                            required
                            v-model="fillRGB"
                            @input="$_updateFill($event)"
                        ></color-picker>
                    </b-col>
                </b-row>
                <b-row v-if="hasStroke">
                    <b-col
                        cols="6"
                        md="4"
                        class="d-flex"
                        style="padding-right: 50px"
                        ><label for="stroke">{{
                            $t('styling.lineColor')
                        }}</label></b-col
                    >
                    <b-col>
                        <color-picker
                            id="stroke"
                            name="stroke"
                            required
                            v-model="strokeRGB"
                            @input="$_updateStroke($event)"
                        ></color-picker>
                    </b-col>
                </b-row>
                <b-row v-if="hasStrokeWidth">
                    <b-col cols="6" md="4"
                        ><label for="stroke_width">{{
                            $t('styling.lineWidth')
                        }}</label></b-col
                    >
                    <b-col class="d-flex">
                        <input
                            id="stroke_width"
                            type="range"
                            class="form-range"
                            min="0"
                            max="5"
                            step="0.2"
                            v-model="localSettings.strokeWidthSvg"
                        />
                        <span class="pl-1">
                            {{ `${localSettings.strokeWidthSvg}` }}
                        </span>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="6" md="4"
                        ><label for="stroke_width">{{
                            $t('styling.preview')
                        }}</label></b-col
                    >
                    <b-col>
                        <svg-loader
                            :source="selectedSource"
                            :settings="previewSettings"
                            @loaded="$_svgLoaded($event)"
                        >
                        </svg-loader>
                    </b-col>
                </b-row>
            </template>
        </b-container>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import { ICON_UNITS } from '../../constants/api';
    import XSelect from '../../components/BaseComponents/x-select.vue';
    import SvgLoader from '../../components/BaseComponents/SvgLoader.vue';
    import ColorPicker from '../../components/ColorPicker.vue';
    import { RGBAToHexAndA, convertHexToRgbAValue } from '../../services/utilities';

    export default {
        components: {
            'svg-loader': SvgLoader,
            'color-picker': ColorPicker,
            'x-select': XSelect
        },
        props: {
            hasPointGeometry: { type: Boolean, required: false, default: false },
            settings: { type: Object, required: true },
            source: { type: String, required: false, default: '' }
        },
        data: function () {
            return {
                hasFill: false,
                hasFillOpacity: false,
                hasStroke: false,
                hasStrokeOpacity: false,
                hasStrokeWidth: false,
                // local values
                localSettings: null,
                fieldInputOptions: [],
                availableSvgs: [],
                selectedSource: '',
                fillRGB: '',
                strokeRGB: '',
                // min and max icon size for pixels
                sizeMin: 9,
                sizeMax: 100,
                // const
                iconUnits: ICON_UNITS
            };
        },
        created() {
            this.init();
        },
        computed: {
            ...mapGetters(['getAvailableSvgFiles']),
            previewSettings() {
                if (this.localSettings.sizeUnits === ICON_UNITS.pixels) {
                    return this.localSettings;
                }
                const metersPreviewSettings = JSON.parse(
                    JSON.stringify(this.localSettings)
                );
                metersPreviewSettings.sizeSvg = 20;
                return metersPreviewSettings;
            }
        },
        methods: {
            async init() {
                if (this.source) {
                    this.selectedSource = this.source;
                } else if (this.localSettings.nameSvg) {
                    this.$_setSvgNameAndSource(this.localSettings.nameSvg);
                }
            },
            $_updateSizeUnits(newVal) {
                if (newVal === ICON_UNITS.meters) {
                    this.sizeMin = 1;
                    this.sizeMax = 10;
                } else {
                    this.sizeMin = 9;
                    this.sizeMax = 100;
                }
                if (this.localSettings.sizeSvg > this.sizeMax) {
                    this.localSettings.sizeSvg = this.sizeMax;
                }
                if (this.localSettings.sizeSvg < this.sizeMin) {
                    this.localSettings.sizeSvg = this.sizeMin;
                }
            },
            $_updateStroke(newVal) {
                const color = RGBAToHexAndA(newVal);
                this.strokeRGB = newVal;
                this.localSettings.strokeSvg = color.hex;
                this.localSettings.strokeOpacitySvg = color.a;
            },
            $_updateFill(newVal) {
                const color = RGBAToHexAndA(newVal);
                this.fillRGB = newVal;
                this.localSettings.fillSvg = color.hex;
                this.localSettings.fillOpacitySvg = color.a;
            },
            $_svgLoaded(event) {
                const mySvg = event.outerHTML;
                this.hasFill = mySvg.includes('"param(fill)"');
                this.hasFillOpacity = mySvg.includes('"param(fill-opacity)"');
                this.hasStroke = mySvg.includes('"param(outline)"');
                this.hasStrokeOpacity = mySvg.includes('"param(outline-opacity)"');
                this.hasStrokeWidth = mySvg.includes('"param(outline-width)"');
            },
            $_setSvgNameAndSource(event) {
                this.localSettings.nameSvg = event;
                // Get URL for selected SVG name
                if (event) {
                    const svg = this.getAvailableSvgFiles.find(
                        (a) => a.fileName === event
                    );
                    this.selectedSource = svg ? svg.fileUrl : '';
                } else {
                    this.selectedSource = '';
                }
            }
        },
        watch: {
            getAvailableSvgFiles: {
                handler: function (newVal) {
                    this.fieldInputOptions = newVal.map((v) => ({
                        text: v.fileName,
                        value: v.fileName
                    }));
                },
                deep: true,
                immediate: true
            },
            source(newValue) {
                this.selectedSource = this.source;
            },
            settings: {
                handler: function (newVal) {
                    this.localSettings = JSON.parse(JSON.stringify(newVal));
                    // Set fillSvg value to rgba
                    if (newVal.fillSvg) {
                        this.fillRGB = convertHexToRgbAValue(newVal.fillSvg);
                    } else {
                        // black as default
                        this.fillRGB = 'rgba(255,255,255,1)';
                    }

                    // set strokeSvg value to rgba
                    if (newVal.strokeSvg) {
                        this.strokeRGB = convertHexToRgbAValue(newVal.strokeSvg);
                    } else {
                        // white as default
                        this.strokeRGB = 'rgba(0,0,0,1)';
                    }

                    if (!newVal.sizeSvg) {
                        this.localSettings.sizeSvg = 15;
                    }
                    if (!newVal.sizeUnits) {
                        this.localSettings.sizeUnits = ICON_UNITS.pixels;
                    }
                    if (!newVal.strokeOpacitySvg) {
                        this.localSettings.strokeOpacitySvg = 1;
                    }
                    if (!newVal.fillOpacitySvg) {
                        this.localSettings.fillOpacitySvg = 1;
                    }
                    if (!newVal.strokeWidthSvg) {
                        this.localSettings.strokeWidthSvg = 1;
                    }
                    this.$_updateSizeUnits(this.localSettings.sizeUnits);
                },
                deep: true,
                immediate: true
            },
            localSettings: {
                handler: function (newVal) {
                    if (JSON.stringify(newVal) === JSON.stringify(this.settings)) {
                        return;
                    }
                    this.$emit('update', newVal);
                },
                deep: true
            }
        }
    };
</script>

<style scoped>
.form-inline label {
    justify-content: start;
}

.row {
    padding-bottom: 15px;
}
</style>
